import { type ButtonHTMLAttributes, forwardRef } from 'react';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'outline' | 'ghost' | 'danger' | 'success';
  size?: 'sm' | 'md' | 'lg';
  isLoading?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      children,
      variant = 'primary',
      size = 'md',
      isLoading = false,
      disabled,
      className = '',
      type = 'button',
      ...props
    },
    ref,
  ) => {
    // Alvo de toque mínimo de 44px garantido para tamanhos md e lg, e foco visível WCAG AA
    const baseStyles =
      'inline-flex items-center justify-center font-medium rounded-xl transition-all duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-orange focus-visible:ring-offset-2 focus-visible:ring-offset-navy active:scale-[0.98] disabled:opacity-50 disabled:pointer-events-none disabled:cursor-not-allowed select-none';

    const variantStyles = {
      primary:
        'bg-brand-orange text-white hover:bg-brand-orangeSecondary shadow-md hover:shadow-lg shadow-brand-orange/20',
      secondary:
        'bg-navy-light text-brand-offWhite hover:bg-navy-light/80 border border-white/10 hover:border-white/20',
      outline:
        'border-2 border-brand-orange text-brand-orange hover:bg-brand-orange/10 focus-visible:bg-brand-orange/15',
      ghost:
        'text-brand-offWhite hover:bg-white/5 hover:text-white',
      danger:
        'bg-feedback-danger text-white hover:bg-feedback-danger/90 shadow-md',
      success:
        'bg-feedback-success text-white hover:bg-feedback-success/90 shadow-md',
    };

    const sizeStyles = {
      sm: 'px-3 py-1.5 text-xs min-h-[36px] min-w-[36px]',
      md: 'px-5 py-2.5 text-sm min-h-[44px] min-w-[44px]',
      lg: 'px-7 py-3.5 text-base font-semibold min-h-[48px] min-w-[48px]',
    };

    return (
      <button
        ref={ref}
        type={type}
        disabled={disabled || isLoading}
        aria-busy={isLoading}
        className={`${baseStyles} ${variantStyles[variant]} ${sizeStyles[size]} ${className}`}
        {...props}
      >
        {isLoading ? (
          <span className="flex items-center gap-2">
            <svg
              className="h-4 w-4 animate-spin text-current"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              />
            </svg>
            <span>Carregando...</span>
          </span>
        ) : (
          children
        )}
      </button>
    );
  },
);

Button.displayName = 'Button';
